import type { DeclarationBlocked, DeclarationExit } from '@api/vendor-portal';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { formatNumber } from '@utils/materialQuantity';
import { type FC, useMemo } from 'react';

import styles from './DeclarationBlockedPanel.module.css';

interface ShortfallRow {
    materialId: string;
    materialName?: string | null;
    required: number;
    available: number;
    shortfall: number;
    inQc: number;
}

interface DeclarationBlockedPanelProps {
    blocked: DeclarationBlocked | null;
    /** What the run was going to make, so "declare less" can say what it would become. */
    productNameFor: (productId: string) => string;
    onClose: () => void;
    onReleaseQc: (materialId: string) => void;
    onRecordInward: (materialIds: string[]) => void;
    onEnterActuals: (materialIds: string[]) => void;
    onDeclareLess: (outputs: Array<{ productId: string; quantity: number }>) => void;
}

/**
 * What each exit is FOR — the diagnosis, not the instruction.
 *
 * "Not enough stock" is a symptom, and these are four different diseases. Naming the situation
 * rather than the click is what lets somebody recognise their own morning in one of them and
 * stop reading; a list of four verbs would make them try each in turn.
 */
const EXIT_COPY: Record<DeclarationExit, { label: string; when: string }> = {
    RECORD_INWARD: {
        label: 'Record what arrived',
        when: 'The truck came and the entry did not. Key the delivery, then declare.',
    },
    RELEASE_QC: {
        label: 'Release it from QC',
        when: 'The material is here but nobody has passed it yet, so nothing may draw on it.',
    },
    ENTER_ACTUAL_CONSUMPTION: {
        label: 'Say what was really used',
        when: 'The recipe overstates it. Enter the actual amounts — the gap is recorded for the brand.',
    },
    REDUCE_QUANTITY: {
        label: 'Declare what the stock supports',
        when: 'The run really was smaller. Record that now and the rest when the material lands.',
    },
};

/**
 * The declaration stopped, and why.
 *
 * A DOOR, NOT A WALL. Stock is never allowed to go negative, so this run cannot be recorded as
 * it stands — but a manufacturer who is told only "insufficient stock" has been handed a dead
 * end in the middle of a shift, and the reliable next move from a dead end is to stop recording
 * and write it in a notebook. Every way out that actually exists is offered here as one tap.
 *
 * THE EXITS SELF-DIAGNOSE. Recording the inward fixes a lagging entry; entering actuals fixes a
 * recipe that overstates; and if the actuals STILL exceed what is here, the inward really is
 * missing and the block correctly holds. Whichever one somebody reaches for teaches the system
 * something true, which is why the block is worth more than a silent override would be.
 *
 * NOTHING HAS BEEN WRITTEN. Said once, plainly, at the top — because the single worst outcome
 * here is somebody believing half the run went in and declaring the remainder on top of it.
 */
const DeclarationBlockedPanel: FC<DeclarationBlockedPanelProps> = ({
    blocked,
    productNameFor,
    onClose,
    onReleaseQc,
    onRecordInward,
    onEnterActuals,
    onDeclareLess,
}) => {
    const rows = useMemo<ShortfallRow[]>(
        () =>
            (blocked?.shortfalls ?? []).map((line) => ({
                ...line,
                inQc: blocked?.releasableByQc[line.materialId] ?? 0,
            })),
        [blocked],
    );

    const columns = useMemo<DataSheetColumn<ShortfallRow>[]>(
        () => [
            {
                key: 'materialId',
                header: 'Material',
                width: 260,
                sticky: true,
                render: (value: string, row) => row.materialName ?? value,
                valueAccessor: (row) => row.materialName ?? row.materialId,
                csvValue: (value: string, row) => row.materialName ?? value,
            },
            {
                key: 'required',
                header: 'Needed',
                width: 110,
                align: 'right',
                render: (value: number) => formatNumber(value, 1),
            },
            {
                key: 'available',
                header: 'QC approved',
                width: 110,
                align: 'right',
                // The number that surprises people: stock can be present and still not usable.
                render: (value: number) => (
                    <Text fz={13} c={value > 0 ? neutralColors.textPrimary : neutralColors.textMuted}>
                        {formatNumber(value, 1)}
                    </Text>
                ),
            },
            {
                key: 'inQc',
                header: 'Waiting on QC',
                width: 130,
                align: 'right',
                render: (value: number) =>
                    value > 0 ? (
                        <Text fz={13} c={semanticColors.warning.dark}>
                            {formatNumber(value, 1)}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            {
                key: 'shortfall',
                header: 'Short by',
                width: 110,
                align: 'right',
                render: (value: number) => (
                    <Text fz={13} fw={600} c={semanticColors.critical.primary}>
                        {formatNumber(value, 1)}
                    </Text>
                ),
            },
        ],
        [],
    );

    if (!blocked) return null;

    const materialIds = blocked.shortfalls.map((line) => line.materialId);

    const runExit = (exit: DeclarationExit) => {
        switch (exit) {
            case 'RECORD_INWARD':
                return onRecordInward(materialIds);
            case 'RELEASE_QC': {
                const withQc = blocked.shortfalls.find(
                    (line) => (blocked.releasableByQc[line.materialId] ?? 0) > 0,
                );
                return onReleaseQc(withQc?.materialId ?? materialIds[0]);
            }
            case 'ENTER_ACTUAL_CONSUMPTION':
                return onEnterActuals(materialIds);
            case 'REDUCE_QUANTITY':
                return onDeclareLess(blocked.maxDeclarable ?? []);
        }
    };

    return (
        <Modal
            opened
            onClose={onClose}
            size="lg"
            title={
                <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                    Not enough material for this run
                </Text>
            }
        >
            <Stack gap={20}>
                <div className={styles.reassurance}>
                    <Text fz={13} c={neutralColors.textPrimary}>
                        Nothing has been recorded. The run is still here to submit once one of
                        these is sorted out.
                    </Text>
                </div>

                <DataSheet<ShortfallRow>
                    data={rows}
                    columns={columns}
                    title="What is short"
                    getRowKey={(row) => row.materialId}
                    maxHeight="auto"
                />

                <Stack gap={12}>
                    <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                        What to do about it
                    </Text>

                    {blocked.exits.map((exit) => (
                        <div key={exit} className={styles.exit}>
                            <div className={styles.exitText}>
                                <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                                    {EXIT_COPY[exit].label}
                                    {exit === 'REDUCE_QUANTITY' && blocked.maxDeclarable && (
                                        <Text component="span" fz={13} c={neutralColors.textSecondary}>
                                            {' — '}
                                            {blocked.maxDeclarable
                                                .map(
                                                    (output) =>
                                                        `${output.quantity} ${productNameFor(output.productId)}`,
                                                )
                                                .join(', ')}
                                        </Text>
                                    )}
                                </Text>
                                <Text fz={12} c={neutralColors.textSecondary}>
                                    {EXIT_COPY[exit].when}
                                </Text>
                            </div>
                            <Button
                                size="xs"
                                variant={ButtonVariant.DEFAULT}
                                onClick={() => runExit(exit)}
                            >
                                Do this
                            </Button>
                        </div>
                    ))}
                </Stack>

                <Group justify="flex-end">
                    <Button variant={ButtonVariant.SUBTLE} onClick={onClose}>
                        Back to the run
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default DeclarationBlockedPanel;
